/** Beneficiary detail slide-over — 2026-09-17 redesign.
 *
 * The campaign beneficiary list used to cram everything (contact info,
 * project, a live status <select>, Schedule/Call-now/Remove buttons,
 * the scheduled date) into every row at once — "too much and killing
 * my eyes." The list row is now a compact summary; clicking it opens
 * this drawer with everything else: full identity + address + custom
 * fields, the status changer, all the actions, and the beneficiary's
 * session/communications history ("how many calls made with this
 * beneficiary").
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { SiteCards } from "@/components/SiteCards";
import { worksitesOf } from "@/components/worksites";
import { ApiPaths, api } from "@/lib/api";
import { describeApiError } from "@/lib/apiErrors";
import { formatDateTime } from "@/lib/format";
import { displayPhone } from "@/lib/phone";
import type { Contact, FieldDefinition, Lead, LeadStatus } from "@/lib/types";

import { resolveFieldSchema } from "./api";
import { BeneficiarySessions } from "./BeneficiarySessions";
import { LastContactedLine } from "./ContactedBy";
import { DECLINED_STATUSES, STATUS_STYLES, STATUS_VALUES } from "./leadStatus";
import { MembershipFieldsCard } from "./MembershipFieldsEditor";
import { MembershipPartiesEditor } from "./MembershipPartiesEditor";

export function LeadStatusPill({ status }: { status: LeadStatus }) {
  const { t } = useTranslation();
  return (
    <span
      className={
        "inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium " +
        (STATUS_STYLES[status] ?? STATUS_STYLES.new)
      }
    >
      {t(`leads.status.${status}`)}
    </span>
  );
}

export function BeneficiaryDetailDrawer({
  lead,
  campaignId,
  onClose,
  onEdit,
  onSchedule,
  onRemove,
}: {
  lead: Lead;
  campaignId: string;
  onClose: () => void;
  onEdit: () => void;
  onSchedule: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [pendingStatus, setPendingStatus] = useState<LeadStatus | null>(null);
  const [reason, setReason] = useState("");
  const [partiesOpen, setPartiesOpen] = useState(false);

  const contact = useQuery({
    queryKey: ["contact", lead.contact],
    queryFn: async () => (await api.get<Contact>(ApiPaths.contact(lead.contact))).data,
  });

  const country = (contact.data?.country || "").toUpperCase();
  const contactSchema = useQuery({
    queryKey: ["fields-schema", "contact", country, campaignId],
    queryFn: () =>
      resolveFieldSchema({ applies_to: "contact", country, campaign: campaignId }),
    enabled: !!contact.data,
  });
  const contactCustomRows: FieldDefinition[] = (contactSchema.data ?? []).filter(
    (fd) => (contact.data?.custom_fields_jsonb?.[fd.key] ?? "") !== "",
  );

  const restore = useMutation({
    mutationFn: async () => {
      await api.post(ApiPaths.campaignLeadRestore(campaignId, lead.id));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-leads", campaignId] });
    },
  });
  const update = useMutation({
    mutationFn: async (payload: { status: LeadStatus; outcome_reason?: string }) => {
      const res = await api.patch<Lead>(
        ApiPaths.campaignLead(campaignId, lead.id),
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-leads", campaignId] });
      setPendingStatus(null);
      setReason("");
    },
  });
  const callNow = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ session_id: string; join_url: string }>(
        ApiPaths.campaignLeadCallNow(campaignId, lead.id),
      );
      return res.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["campaign-leads", campaignId] });
      navigate(data.join_url || `/sessions/${data.session_id}`);
    },
  });

  function onStatusChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value as LeadStatus;
    if (DECLINED_STATUSES.has(next)) {
      setPendingStatus(next);
      return;
    }
    update.mutate({ status: next });
  }

  const c = contact.data;
  const personName = `${lead.contact_first_name} ${lead.contact_last_name}`.trim();
  const displayName = lead.contact_is_business
    ? c?.legal_name || personName
    : personName;
  const addressParts = c
    ? [c.address_line, [c.postal_code, c.city].filter(Boolean).join(" "), c.country].filter(
        Boolean,
      )
    : [];
  const hasCompanyContact =
    lead.contact_is_business &&
    c &&
    (c.company_contact_name || c.company_contact_email || c.company_contact_phone);

  return (
    <>
      {/* Backdrop — click to close, same as every other overlay in the app. */}
      <div
        className="fixed inset-0 z-50 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("leads.detailTitle", "Beneficiary details")}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l border-nb-border bg-nb-surface shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-nb-border px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-semibold text-nb-text">
                {displayName || "—"}
              </h2>
              {lead.contact_is_business && (
                <span className="pill text-[10px]">
                  {t("contacts.businessHeading", "Legal entity")}
                </span>
              )}
              <LeadStatusPill status={lead.status} />
              {/* When the appointment is, next to who it's with. It was
                  already in the drawer — ~140 lines down in the details
                  list, which is no use to someone asking "when am I
                  seeing them?". */}
              {lead.next_scheduled_for && (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-nb-mint/40 bg-nb-mint/10 px-2 py-0.5 text-[11px] font-medium text-nb-mint"
                  data-testid="lead-scheduled-chip"
                >
                  {t("leads.col.scheduled")}:{" "}
                  {formatDateTime(lead.next_scheduled_for)}
                </span>
              )}
            </div>
            {lead.contact_is_business && personName && (
              <p className="text-xs text-nb-text-3">{personName}</p>
            )}
          </div>
          <button
            type="button"
            className="btn-ghost shrink-0 text-xs"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
          >
            ✕
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Quick actions — the buttons that used to live stacked in the
              row's status column. */}
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-ghost text-xs" onClick={onEdit}>
              {t("leads.editButton", "Edit")}
            </button>
            {!lead.is_archived && (
              <button type="button" className="btn-ghost text-xs" onClick={onSchedule}>
                {lead.next_scheduled_for
                  ? t("leads.rescheduleButton", "Reschedule")
                  : t("leads.scheduleButton")}
              </button>
            )}
            {!lead.is_archived && (
              <button
                type="button"
                className="btn-ghost text-xs text-nb-mint"
                onClick={() => callNow.mutate()}
                disabled={callNow.isPending}
                title={t(
                  "leads.callNowTooltip",
                  "Start the inspection now and send the join link",
                )}
                data-testid="lead-call-now-button"
              >
                {callNow.isPending
                  ? t("leads.callingNow", "Starting…")
                  : t("leads.callNowButton", "Call now")}
              </button>
            )}
            {lead.is_archived ? (
              <button
                type="button"
                className="btn-ghost text-xs"
                disabled={restore.isPending}
                onClick={() => restore.mutate()}
                data-testid="lead-restore-button"
              >
                {restore.isPending
                  ? t("common.restoring", "Restoring…")
                  : t("leads.restoreButton", "Restore")}
              </button>
            ) : (
              <button
                type="button"
                className="btn-ghost text-xs text-cvp-warn hover:text-cvp-danger"
                onClick={onRemove}
                disabled={lead.has_active_session}
                title={
                  lead.has_active_session
                    ? t(
                        "leads.removeBlockedTooltip",
                        "Cancel or end the active session first",
                      )
                    : t(
                        "leads.removeTooltip",
                        "Remove from this campaign (keeps the contact)",
                      )
                }
                data-testid="lead-remove-button"
              >
                {t("leads.removeButton", "Remove")}
              </button>
            )}
          </div>
          {callNow.isError && (
            <p className="text-xs text-cvp-danger">
              {describeApiError(
                callNow.error,
                t("leads.callNowError", "Couldn't start the call."),
              )}
            </p>
          )}
          {lead.is_archived && (
            <p className="text-xs italic text-nb-text-3">
              {t("leads.removedAt", "Removed")}{" "}
              {lead.archived_at ? formatDateTime(lead.archived_at) : ""}
              {lead.removed_by_email ? ` · ${lead.removed_by_email}` : ""}
            </p>
          )}

          {/* Status changer */}
          <section className="card space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
              {t("leads.col.status")}
            </h3>
            {pendingStatus ? (
              <div className="space-y-1.5">
                <input
                  type="text"
                  className="input text-xs"
                  placeholder={t("leads.outcomeReasonPlaceholder")}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  autoFocus
                />
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    className="btn-primary text-xs"
                    disabled={!reason.trim() || update.isPending}
                    onClick={() =>
                      update.mutate({
                        status: pendingStatus,
                        outcome_reason: reason.trim(),
                      })
                    }
                  >
                    {t("common.save")}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={() => {
                      setPendingStatus(null);
                      setReason("");
                    }}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <select
                className="input text-xs"
                value={lead.status}
                onChange={onStatusChange}
                disabled={update.isPending}
                aria-label={t("leads.changeStatusLabel", "Change status")}
              >
                {STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>
                    {t(`leads.status.${s}`)}
                  </option>
                ))}
              </select>
            )}
            {lead.outcome_reason && (
              <p className="text-xs text-nb-text-3">{lead.outcome_reason}</p>
            )}
            {lead.next_scheduled_for && (
              <p className="text-xs text-nb-text-2">
                {t("leads.col.scheduled", "Scheduled at")}:{" "}
                <span className="font-medium text-nb-text">
                  {formatDateTime(lead.next_scheduled_for)}
                </span>
              </p>
            )}
            {/* When they were last reached, and by what. */}
            <LastContactedLine
              membership={lead}
              testId="lead-contacted-detail"
            />
          </section>

          {/* Identity + address + legal entity */}
          <section className="card space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
              {t("contacts.identityHeading")}
            </h3>
            {contact.isLoading ? (
              <p className="text-xs text-nb-text-3">{t("common.loading")}</p>
            ) : (
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
                {lead.contact_email && (
                  <>
                    <dt className="text-nb-text-3">{t("contacts.col.email")}</dt>
                    <dd>{lead.contact_email}</dd>
                  </>
                )}
                {lead.contact_phone && (
                  <>
                    <dt className="text-nb-text-3">{t("contacts.col.phone")}</dt>
                    <dd className="font-mono">{displayPhone(lead.contact_phone)}</dd>
                  </>
                )}
                {addressParts.length > 0 && (
                  <>
                    <dt className="text-nb-text-3">{t("party.addressLine")}</dt>
                    <dd>{addressParts.join(", ")}</dd>
                  </>
                )}
                {(lead.project_category && lead.project_category !== "generic") ||
                lead.project_code ? (
                  <>
                    <dt className="text-nb-text-3">
                      {t("leads.col.project", "Project")}
                    </dt>
                    <dd>
                      {lead.project_category && lead.project_category !== "generic" && (
                        <span className="mr-1 rounded bg-nb-surface px-1.5 py-0.5 font-mono text-[10px] uppercase text-nb-text-2">
                          {lead.project_category}
                        </span>
                      )}
                      {lead.project_code}
                    </dd>
                  </>
                ) : null}
                {c?.is_business && c.registration_number && (
                  <>
                    <dt className="text-nb-text-3">{t("party.registrationNumber")}</dt>
                    <dd className="font-mono">{c.registration_number}</dd>
                  </>
                )}
                {c?.is_business && c.vat_id && (
                  <>
                    <dt className="text-nb-text-3">{t("party.vatId")}</dt>
                    <dd className="font-mono">{c.vat_id}</dd>
                  </>
                )}
              </dl>
            )}
            {hasCompanyContact && c && (
              <div className="border-t border-nb-border pt-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-nb-text-3">
                  {t("contacts.companyContactHeading", "Company contact")}
                </p>
                <dl className="mt-1 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
                  {c.company_contact_name && (
                    <>
                      <dt className="text-nb-text-3">
                        {t("contacts.companyContactName", "Contact name")}
                      </dt>
                      <dd>{c.company_contact_name}</dd>
                    </>
                  )}
                  {c.company_contact_email && (
                    <>
                      <dt className="text-nb-text-3">{t("contacts.col.email")}</dt>
                      <dd>{c.company_contact_email}</dd>
                    </>
                  )}
                  {c.company_contact_phone && (
                    <>
                      <dt className="text-nb-text-3">{t("contacts.col.phone")}</dt>
                      <dd className="font-mono">
                        {displayPhone(c.company_contact_phone)}
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            )}
            {lead.notes_intake && (
              <div className="border-t border-nb-border pt-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-nb-text-3">
                  {t("leads.col.notesIntake")}
                </p>
                <p className="whitespace-pre-wrap text-xs text-nb-text-2">
                  {lead.notes_intake}
                </p>
              </div>
            )}
            <button
              type="button"
              className="text-xs text-nb-mint hover:underline"
              onClick={() => setPartiesOpen((v) => !v)}
            >
              {partiesOpen ? "▾" : "▸"} {lead.parties?.length ?? 0}{" "}
              {t("leads.col.partiesShort", "parties")}
            </button>
            {partiesOpen && (
              <MembershipPartiesEditor
                campaignId={campaignId}
                membershipId={lead.id}
                initialParties={lead.parties ?? []}
              />
            )}
          </section>

          {/* Sites — its own box rather than a sub-block of Identity.
              Reported 2026-09-23: on a multi-site beneficiary these are
              the thing the operator is actually working from, and they
              were buried under the email and phone. Numbered so "site 2"
              means the same on every surface. */}
          {c && worksitesOf(c.addresses).length > 0 && (
            <section className="card space-y-2">
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-nb-text-2">
                {t("sessionDetail.addresses", {
                  count: worksitesOf(c.addresses).length,
                })}
              </h3>
              <SiteCards
                addresses={worksitesOf(c.addresses)}
                campaign={campaignId}
              />
            </section>
          )}

          {/* Custom fields — membership-scoped (editable here) + contact-
              scoped (read-only, mirrors the Contact detail page). */}
          <section className="card space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
              {t("leads.campaignFields.heading", "Campaign fields")}
            </h3>
            <MembershipFieldsCard
              campaign={campaignId}
              membershipId={lead.id}
              values={lead.custom_fields_jsonb}
              canEdit
            />
            {contactCustomRows.length > 0 && (
              <div className="border-t border-nb-border pt-2">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-nb-text-3">
                  {t("contacts.customFieldsHeading", "Custom fields")}
                </p>
                <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
                  {contactCustomRows.map((fd) => (
                    <Fragment key={fd.id}>
                      <dt className="text-nb-text-3">{fd.label}</dt>
                      <dd>{String(c?.custom_fields_jsonb?.[fd.key] ?? "")}</dd>
                    </Fragment>
                  ))}
                </dl>
              </div>
            )}
          </section>

          {/* Sessions + delivery timeline — "how many calls made with this
              beneficiary" answered directly. */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
              {t("nav.sessions")}
            </h3>
            <BeneficiarySessions
              campaignId={campaignId}
              membershipId={lead.id}
              onReschedule={onSchedule}
            />
          </section>
        </div>
      </div>
    </>
  );
}
